import {
  useRef,
  useState,
  ReactNode,
  KeyboardEvent,
  useLayoutEffect,
} from 'react';

import { isMobile } from 'hooks';
import { formatPrice } from 'utils/helpers';

import * as Styles from './styles';
import { RecordColumn, RecordTableProps } from './types';

const DASH = '—';

// A column's share of the row when it sets no weight of its own.
const DEFAULT_WEIGHT: Record<string, number> = {
  date: 1,
  flex: 2.2,
  amount: 1,
  nowrap: 1.4,
  compact: 1.2,
};

// The backend sends '-' for a missing name; it is shown as the muted dash.
// One-line columns: when one overflows, the table shows cards instead.
const ONE_LINE = ['date', 'nowrap', 'amount'];

const isEmpty = (value: ReactNode) =>
  value === undefined ||
  value === null ||
  value === false ||
  (typeof value === 'string' && ['', '-'].includes(value.trim()));

// An empty value is a muted dash, never a blank cell.
const show = (value: ReactNode) =>
  isEmpty(value) ? <Styles.Empty>{DASH}</Styles.Empty> : value;

const activate =
  (action: () => void) => (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

// The records behind one P&L line, in the line drawers. Like the app's lists,
// each column takes a proportional share of the row (text columns wrap,
// one-line columns never do), amounts are right-aligned. On a phone, or when
// a one-line value does not fit its share, each record is a card.
export const RecordTable = <T,>({
  rows,
  total,
  getKey,
  onRowClick,
  getRowLabel,
  isRowClickable,
  columns: allColumns,
  totalLabel = 'Total',
}: RecordTableProps<T>) => {
  const mobile = isMobile();
  // A table that does not fit (many columns on a narrow iPad) shows as the
  // phone cards instead of scrolling sideways; it comes back as a table
  // once the drawer is wide enough for it again.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [neededWidth, setNeededWidth] = useState<number>();
  const [wrapWidth, setWrapWidth] = useState<number>();
  const [tooWide, setTooWide] = useState(false);
  const stacked = mobile || tooWide;

  // Back to a table only with 24px to spare, so a scrollbar that comes and
  // goes can never flip it back and forth.
  useLayoutEffect(() => {
    if (
      tooWide &&
      neededWidth !== undefined &&
      wrapWidth !== undefined &&
      wrapWidth >= neededWidth + 24
    ) {
      setTooWide(false);
    }
  }, [tooWide, neededWidth, wrapWidth]);

  // New records (another line in the same drawer) or late-loading fonts
  // can change what fits: measure the table again. Neither depends on the
  // table's own layout, so this cannot flip back and forth.
  const rowsKey = rows.map(getKey).join('|');
  useLayoutEffect(() => {
    setTooWide(false);
  }, [rowsKey]);
  useLayoutEffect(() => {
    const fonts = wrapRef.current?.ownerDocument.fonts;
    if (!fonts?.addEventListener) {
      return undefined;
    }
    const remeasure = () => setTooWide(false);
    fonts.addEventListener('loadingdone', remeasure);
    return () => fonts.removeEventListener('loadingdone', remeasure);
  }, []);

  // Track the space the table has.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) {
      return undefined;
    }
    const update = () => setWrapWidth(wrap.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(update);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  // A cell whose one-line text does not fit its share of the row: the
  // grid needs (roughly) that much more width; until then, cards.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || tooWide) {
      return;
    }
    const cells = Array.from(
      wrap.querySelectorAll<HTMLElement>('[data-overflow-check]')
    );
    const ratio = Math.max(
      1,
      ...cells.map((cell) =>
        cell.clientWidth ? cell.scrollWidth / cell.clientWidth : 1
      )
    );
    if (ratio > 1.01) {
      setNeededWidth(Math.ceil(wrap.clientWidth * ratio));
      setTooWide(true);
    }
  });
  const columns = allColumns;
  // Each column gets a proportional share of the row (like the app's lists);
  // the receipt icon has a fixed slot.
  const template = columns
    .map(({ kind, weight }) =>
      kind === 'icon'
        ? '40px'
        : `minmax(0, ${weight ?? DEFAULT_WEIGHT[kind ?? 'nowrap']}fr)`
    )
    .join(' ');
  const countText = `${rows.length} record${rows.length === 1 ? '' : 's'}`;
  const canClick = (row: T) =>
    !!onRowClick && (isRowClickable ? isRowClickable(row) : true);
  const rowProps = (row: T) =>
    canClick(row)
      ? {
          tabIndex: 0,
          role: 'button',
          $clickable: true,
          onClick: () => onRowClick?.(row),
          onKeyDown: activate(() => onRowClick?.(row)),
          'aria-label': getRowLabel ? getRowLabel(row) : undefined,
        }
      : {};

  if (stacked) {
    const dateColumn = columns.find(({ kind }) => kind === 'date');
    const amountColumn = columns.find(({ kind }) => kind === 'amount');
    const fields = columns.filter(
      (column) => column !== dateColumn && column !== amountColumn
    );
    return (
      <div ref={wrapRef}>
        <Styles.Cards data-testid="record-cards">
          {rows.map((row, index) => (
            <Styles.Card
              key={getKey(row)}
              $odd={index % 2 === 0}
              {...rowProps(row)}
            >
              <Styles.CardTop>
                <span>{dateColumn ? show(dateColumn.value(row)) : null}</span>
                <span>
                  {amountColumn ? show(amountColumn.value(row)) : null}
                </span>
              </Styles.CardTop>
              {fields.map((column) => (
                <Styles.CardField
                  key={column.key}
                  $center={column.kind === 'icon'}
                >
                  <span>{column.title || 'Receipt'}</span>
                  <span>{show(column.value(row))}</span>
                </Styles.CardField>
              ))}
            </Styles.Card>
          ))}
          {!rows.length && <Styles.Empty>No records</Styles.Empty>}
          {total !== undefined && (
            <Styles.TotalCard data-testid="record-total">
              <Styles.CardTop>
                <span>{`${totalLabel} · ${countText}`}</span>
                <span>{formatPrice(total)}</span>
              </Styles.CardTop>
            </Styles.TotalCard>
          )}
        </Styles.Cards>
      </div>
    );
  }

  const amountIndex = columns.findIndex(({ kind }) => kind === 'amount');

  const cell = (column: RecordColumn<T>, row: T) => {
    const value = column.value(row);
    if (column.kind === 'icon') {
      // The receipt button acts on its own, not on the row.
      return (
        <Styles.IconWrap onClick={(event) => event.stopPropagation()}>
          {value}
        </Styles.IconWrap>
      );
    }
    return show(value);
  };

  return (
    <div ref={wrapRef}>
      <Styles.Grid role="table" data-testid="record-table">
        <Styles.HeadRow role="row" $template={template}>
          {columns.map((column) => (
            <Styles.HeadCell
              key={column.key}
              role="columnheader"
              data-overflow-check
              data-kind={column.kind}
            >
              {column.title}
            </Styles.HeadCell>
          ))}
        </Styles.HeadRow>
        {rows.map((row, index) => (
          <Styles.GridRow
            key={getKey(row)}
            $template={template}
            $odd={index % 2 === 0}
            data-testid="record-row"
            {...rowProps(row)}
          >
            {columns.map((column) => (
              <Styles.Cell
                key={column.key}
                data-kind={column.kind}
                data-overflow-check={
                  ONE_LINE.includes(column.kind ?? 'nowrap') || undefined
                }
              >
                {cell(column, row)}
              </Styles.Cell>
            ))}
          </Styles.GridRow>
        ))}
        {!rows.length && (
          <Styles.GridRow $odd $template="1fr">
            <Styles.Empty>No records</Styles.Empty>
          </Styles.GridRow>
        )}
        {total !== undefined && (
          <Styles.TotalRow $template={template} data-testid="record-total">
            <Styles.TotalLabelCell $span={Math.max(amountIndex, 1)}>
              {`${totalLabel} · ${countText}`}
            </Styles.TotalLabelCell>
            {columns.slice(Math.max(amountIndex, 1)).map((column) => (
              <Styles.Cell key={column.key} data-kind={column.kind}>
                {column.kind === 'amount' && formatPrice(total)}
              </Styles.Cell>
            ))}
          </Styles.TotalRow>
        )}
      </Styles.Grid>
    </div>
  );
};

export type { RecordColumn };
